import React from 'react'
import Paper from '@mui/material/Paper';
import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Divider from '@mui/material/Divider';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import TabletMacIcon from '@mui/icons-material/TabletMac';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import { useStore, useSelector } from 'react-redux';

import Header from "./design/header";
import Footer from "./design/footer";
import Body from "./design/body";
import Preview from "./preview";
import Html from "./html";
import Mjml from "./mjml";
import Email from "./email";
import { renderEmailString } from "./emailString";
import ImageProps from "./properties/imageprops";
import TextProps from "./properties/textprops";
import IfProps from "./properties/ifprops";
import IfElseProps from "./properties/ifelseprops";
import MjmlProps from "./properties/mjmlprops";
import { getSelectedRowContent, isImage, isText, isIf, isIfElse, isMjml } from "../selectors";

const DEVICE_WIDTHS = {
  desktop: '100%',
  tablet: '768px',
  mobile: '375px',
};

const MIN_PANEL_WIDTH = 260;
const MAX_CANVAS_MIN = 380;
const HANDLE_WIDTH = 10;
const HANDLE_HEIGHT = 10;
const MIN_EDITOR_HEIGHT = 120;
const MIN_BELOW_EDITOR_HEIGHT = 220;
const DEFAULT_EDITOR_HEIGHT = 280;

const useStyles = makeStyles(theme => ({
  canvas: {
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  resizeHandle: {
    width: HANDLE_WIDTH,
    flexShrink: 0,
    cursor: 'col-resize',
    position: 'relative',
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.divider,
    transition: 'background-color 0.15s',
    '&:hover': {
      backgroundColor: theme.palette.secondary.main,
    },
    '&::before': {
      content: '"⋮⋮"',
      color: '#fff',
      fontSize: '10px',
      lineHeight: 1,
      letterSpacing: '-1px',
      userSelect: 'none',
      pointerEvents: 'none',
    },
  },
  resizeHandleHorizontal: {
    height: HANDLE_HEIGHT,
    flexShrink: 0,
    cursor: 'row-resize',
    position: 'relative',
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.divider,
    transition: 'background-color 0.15s',
    '&:hover': {
      backgroundColor: theme.palette.secondary.main,
    },
    '&::before': {
      content: '"···"',
      color: '#fff',
      fontSize: '10px',
      lineHeight: 1,
      userSelect: 'none',
      pointerEvents: 'none',
    },
  },
  previewToolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
  },
}));

function getTypeLabel({ contentIsImage, contentIsText, contentIsIf, contentIsIfElse, contentIsMjml }) {
  if (contentIsImage) return "Image";
  if (contentIsText) return "Text";
  if (contentIsIf) return "If statement";
  if (contentIsIfElse) return "If Else statement";
  if (contentIsMjml) return "MJML Block";
  return "";
}

const Design = ({ editorOpen, onCloseEditor, resetKey }) => {
  const classes = useStyles();
  const store = useStore();

  const selectedRowContent = useSelector(getSelectedRowContent);
  const contentIsImage = useSelector(isImage);
  const contentIsText = useSelector(isText);
  const contentIsIf = useSelector(isIf);
  const contentIsIfElse = useSelector(isIfElse);
  const contentIsMjml = useSelector(isMjml);

  const [codeTab, setCodeTab] = React.useState(0);
  const [device, setDevice] = React.useState('desktop');
  const [copied, setCopied] = React.useState(false);
  const [panelWidth, setPanelWidth] = React.useState(() =>
    Math.floor((window.innerWidth - 48) / 2)
  );
  const [editorHeight, setEditorHeight] = React.useState(DEFAULT_EDITOR_HEIGHT);

  const containerRef = React.useRef(null);
  const sidebarPaperRef = React.useRef(null);
  const isDragging = React.useRef(false);
  const isDraggingEditor = React.useRef(false);

  function handleDividerMouseDown(e) {
    e.preventDefault();
    isDragging.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    function onMouseMove(e) {
      if (!isDragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const newWidth = rect.right - e.clientX - HANDLE_WIDTH / 2;
      const maxWidth = rect.width - MAX_CANVAS_MIN;
      setPanelWidth(Math.min(maxWidth, Math.max(MIN_PANEL_WIDTH, newWidth)));
    }

    function onMouseUp() {
      isDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    }

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  function handleEditorResizeMouseDown(e) {
    e.preventDefault();
    isDraggingEditor.current = true;
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';

    function onMouseMove(e) {
      if (!isDraggingEditor.current || !sidebarPaperRef.current) return;
      const rect = sidebarPaperRef.current.getBoundingClientRect();
      const newHeight = e.clientY - rect.top;
      const maxHeight = rect.height - MIN_BELOW_EDITOR_HEIGHT;
      setEditorHeight(Math.min(maxHeight, Math.max(MIN_EDITOR_HEIGHT, newHeight)));
    }

    function onMouseUp() {
      isDraggingEditor.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    }

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  function handleCodeTabChange(_, newValue) {
    setCodeTab(newValue);
  }

  function handleDeviceChange(_, newDevice) {
    if (newDevice) setDevice(newDevice);
  }

  async function handleCopyHtml() {
    const html = renderEmailString(store, false);
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([html], { type: 'text/plain' }),
      }),
    ]);
    setCopied(true);
  }

  function handleCopiedClose() {
    setCopied(false);
  }

  const tabStyle = { minHeight: 40, fontSize: '0.72rem', padding: '6px 10px' };
  const typeLabel = getTypeLabel({ contentIsImage, contentIsText, contentIsIf, contentIsIfElse, contentIsMjml });
  const editorKey = selectedRowContent ? `${selectedRowContent.id}-${resetKey}` : null;

  return (
    <div>
      <div ref={containerRef} style={{ display: 'flex' }}>
        {/* Left column: canvas + row editor, stacked */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <div className={classes.canvas}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Header />
              </Grid>
              <Grid item xs={12}>
                <Body />
              </Grid>
              <Grid item xs={12}>
                <Footer />
              </Grid>
            </Grid>
          </div>
        </div>

        {/* Drag handle */}
        <div
          className={classes.resizeHandle}
          onMouseDown={handleDividerMouseDown}
          aria-hidden="true"
        />

        {/* Side panel: Preview / HTML / MJML */}
        <div style={{
          width: panelWidth,
          flexShrink: 0,
          alignSelf: 'flex-start',
          position: 'sticky',
          top: 0,
          height: '100vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <Paper ref={sidebarPaperRef} style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {editorOpen && selectedRowContent && (
              <>
                <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', height: editorHeight }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 16px' }}>
                    <Typography variant="subtitle2">{typeLabel}</Typography>
                    <IconButton size="small" aria-label="Close editor" onClick={onCloseEditor}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </div>
                  <Divider />
                  <div style={{ padding: 16, overflowY: 'auto' }}>
                    {contentIsImage && <ImageProps content={selectedRowContent} key={editorKey} />}
                    {contentIsText && <TextProps content={selectedRowContent} key={editorKey} />}
                    {contentIsIf && <IfProps content={selectedRowContent} key={editorKey} />}
                    {contentIsIfElse && <IfElseProps content={selectedRowContent} key={editorKey} />}
                    {contentIsMjml && <MjmlProps content={selectedRowContent} key={editorKey} />}
                  </div>
                </div>
                <div
                  className={classes.resizeHandleHorizontal}
                  onMouseDown={handleEditorResizeMouseDown}
                  aria-hidden="true"
                />
              </>
            )}
            <Tabs
              value={codeTab}
              onChange={handleCodeTabChange}
              textColor="secondary"
              indicatorColor="secondary"
              style={{ minHeight: 40, flexShrink: 0 }}
            >
              <Tab label="Preview" style={tabStyle} />
              <Tab label="HTML" style={tabStyle} />
              <Tab label="MJML" style={tabStyle} />
            </Tabs>
            <Divider />
            {codeTab === 0 && (
              <div className={classes.previewToolbar}>
                <ToggleButtonGroup
                  value={device}
                  exclusive
                  onChange={handleDeviceChange}
                  size="small"
                  aria-label="preview device size"
                >
                  <ToggleButton value="desktop" aria-label="desktop">
                    <DesktopWindowsIcon fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="tablet" aria-label="tablet">
                    <TabletMacIcon fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="mobile" aria-label="mobile">
                    <PhoneIphoneIcon fontSize="small" />
                  </ToggleButton>
                </ToggleButtonGroup>
                <Button
                  onClick={handleCopyHtml}
                  variant="outlined"
                  color="secondary"
                  size="small"
                  startIcon={<ContentCopyIcon fontSize="small" />}
                >
                  Copy HTML
                </Button>
              </div>
            )}
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {codeTab === 0 && (
                <div style={{
                  overflowX: 'auto',
                  display: 'flex',
                  justifyContent: 'center',
                  backgroundColor: device === 'desktop' ? '#fff' : '#f4f5f7',
                  padding: device === 'desktop' ? 0 : '16px 0',
                  boxShadow: '0 0 0 1px rgba(0,0,0,0.1)',
                }}>
                  <div style={{
                    width: DEVICE_WIDTHS[device],
                    flexShrink: 0,
                    transition: 'width 0.2s ease-in-out',
                    boxShadow: device === 'desktop' ? 'none' : '0 2px 12px rgba(0,0,0,0.15)',
                  }}>
                    <Preview width="100%"><Email /></Preview>
                  </div>
                </div>
              )}
              {codeTab === 1 && (
                <div style={{ padding: 16, overflowX: 'auto' }}>
                  <Html />
                </div>
              )}
              {codeTab === 2 && (
                <div style={{ padding: 16, overflowX: 'auto' }}>
                  <Mjml />
                </div>
              )}
            </div>
          </Paper>
        </div>
      </div>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        open={copied}
        onClose={handleCopiedClose}
        autoHideDuration={3000}
        message="Email HTML copied to clipboard"
        action={
          <IconButton size="small" aria-label="close" color="inherit" onClick={handleCopiedClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </div>
  );
};

export default Design;
